import {
  HeadlessFlatTreeItemProps,
  makeStyles,
  TreeItemValue,
  useHeadlessFlatTree_unstable,
} from "@fluentui/react-components";
import * as React from "react";
import { useEffect, useRef } from "react";
import { PageDocumentRoots } from "../../../api/models/page-document-roots";
import { FlatPathTreeItem } from "../FlatPathTreeExplorer";
import { DocumentPart } from "../../../api/models";
import { DocumentRoot } from "../../../api/models/document-root";
import { UserFunctionEnum } from "../../../api/models/user-function-enum";
import { isNotAuthorizedUser } from "../../../utils/user-function/userFunctionUtils";
import { useAppSelector } from "../../../redux/hooks/hooks";
import { selectCpmUser } from "../../../redux/feature/settings/configurations-slice";
import { DocumentPartWithStatus } from "../../../api/models/document-part-with-status";

type CustomItem = HeadlessFlatTreeItemProps & {
  name: string | React.ReactNode;
  document: DocumentPart;
  permission: boolean;
};

const useStyles = makeStyles({
  container: {
    height: "calc(90vh - 8rem)",
    overflow: "auto",
    paddingBottom: "3.5rem",
    fontSize: "1.25rem",
    fontWeight: "600",
    lineHeight: "1.25rem",
  },
  screenReadersOnly: {
    position: "relative",
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
  },
  fileIconHeaderIcon: {
    padding: 0,
    margin: "9px 10px 0",
    fontSize: "1.25rem",
    color: "#007AC3",
  },
  rowTreeItemBlack: {
    display: "flex",
    color: "var(--color-black)",
    "&:hover, &:focus": {
      backgroundColor: "#e6f2f9",
    },
  },
  rowTreeItemGray: {
    display: "flex",
    opacity: "0.5",
    "&:hover, &:focus": {
      backgroundColor: "#e6f2f9",
    },
  },
  documentName: {
    "&>div": {
      maxWidth: "68vw",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
});

export interface VirtualTreeProps {
  childrenItems: FlatPathTreeItem[];
  loadDocumentPart: (documentRoot: DocumentRoot) => DocumentRoot;
  openDocument: (documentPart: DocumentPart) => DocumentPart;
  virtualScrollAddItems: (page: number) => Promise<void>;
  pagination: PageDocumentRoots;
  firstNewItemValue: string;
  selectedItem?: FlatPathTreeItem;
  showDownloadButton?: boolean;
  documentPartWithStatus: DocumentPartWithStatus[] | null;
}

const useVirtualTree = (props: VirtualTreeProps) => {
  const { childrenItems, pagination, firstNewItemValue, selectedItem } = props;
  const user = useAppSelector(selectCpmUser);
  const notAuthorizedUserForDownload = isNotAuthorizedUser(
    UserFunctionEnum.DOWNLOAD_DOCUMENT_BUTTON,
    user?.userFunctions ?? []
  );
  const isLoadingRef = useRef(false);
  const itemToFocusRef = React.useRef<HTMLDivElement>(null);
  const [itemToFocusValue, setItemToFocusValue] = React.useState<TreeItemValue>();
  const items = React.useMemo<CustomItem[]>(() => refreshItems(), [childrenItems]);
  const styles = useStyles();
  const flatTree = useHeadlessFlatTree_unstable(items);
  const FlatTreeRef = useRef(null);

  useEffect(() => {
    if (
      !isLoadingRef.current &&
      FlatTreeRef.current.scrollHeight <= FlatTreeRef.current.clientHeight &&
      pagination &&
      pagination.number + 1 < pagination.totalPages
    ) {
      console.log("flat treee ");
      fetchMoreItems();
    }
  }, [pagination?.number]);

  useEffect(() => {
    setItemToFocusValue(firstNewItemValue);
  }, [firstNewItemValue]);

  useEffect(() => {
    if (itemToFocusRef.current) {
      itemToFocusRef.current.focus({ preventScroll: true });
      setItemToFocusValue(undefined);
    }
  }, [itemToFocusValue]);

  function refreshItems() {
    return [
      ...childrenItems.map((x) => {
        return { value: x.key, name: x.text, document: x.document, permission: x.readPermission };
      }),
    ];
  }

  function handleClick(documentRoot: DocumentRoot) {
    props.loadDocumentPart(documentRoot);
  }
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const hasReachedEnd = scrollHeight - scrollTop <= clientHeight + 5;
    if (!isLoadingRef.current && hasReachedEnd && pagination?.number + 1 < pagination?.totalPages) {
      fetchMoreItems();
    }
  };

  const fetchMoreItems = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    await props.virtualScrollAddItems(pagination.number + 1);
    isLoadingRef.current = false;
  };

  return {
    flatTree,
    handleScroll,
    styles,
    handleClick,
    itemToFocusValue,
    itemToFocusRef,
    selectedItem,
    notAuthorizedUserForDownload,
    FlatTreeRef,
  };
};

export default useVirtualTree;
